import { Range } from '@attain-apps/range'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as common from '../common'
import type * as dms from '../dms'

import type { Access } from './access'
import type { App } from './app'
import type { Context } from './context'
import type { User } from './user'
import type { Zone } from './zone'

// NOTE: this makes creating a TypeChecker easier
type ScenarioBase = common.Identified<Scenario.Id> &
  common.Named &
  common.Aliased &
  common.Described.Optionally &
  common.CreatedBy<User.Id>
/**
 * Sometimes, a user wants to create multiple versions of an {@link App} where
 * the only thing that is different is the data in the {@link App}
 *
 * For example, maybe a user wants to use an app once against 2020 financials
 * and another time against 2021 financials. Or maybe a user wants to project
 * what the future looks like in an optimistic case and a pessimistic case
 *
 * To support the above and other similar use cases, we have this concept of
 * {@link Scenario}, which can have their own data. However, it is important to
 * note that all data in the {@link Zone} is shared across all {@link App}
 * and {@link Scenario}. So in cases where an {@link App} is working directly
 * with {@link Zone} data, multiple {@link Scenario scenarios} may be reading
 * or overwriting the same data
 */
export interface Scenario extends ScenarioBase {
  /** @see {@link Scenario.Visibility} */
  visibility: Scenario.Visibility
  /** @see {@link Scenario.Period} */
  period: Scenario.Period
  variables: dms.data.query.Variable.Lookup
}
export namespace Scenario {
  /**
   * Identifies a {@link Scenario}
   */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<Id> =
      TypeChecker.isString.usingAlias('Scenario.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * Defines the timeframe of data included in a given scenario, particularly
   * when querying the DMS.
   *
   * Represented in a simple, easy-to-consume, JSON-compatible format to make it
   * easier to transfer between systems (e.g., graphQL, DynamoDB, etc.)
   *
   * Use {@link Scenario.getPeriodRange} to get the {@link Scenario.period} as a
   * Date {@link Range}.
   */
  export type Period = Replace.Properties<
    dms.data.expression.instance.definitions.Range.Params<Date>,
    'start' | 'end',
    string
  >

  /**
   * {@link Scenario} with additional information pertaining to the associated
   * {@link User}
   */
  export interface ForUser extends Scenario, Access.Holder {}

  /**
   * {@link Scenario} that also has the full {@link Context.ForScenario}
   */
  export type InContext = Scenario & Context.ForScenario

  /**
   * Gets the {@link Scenario.period} from the given {@link Scenario} as a Date
   * {@link Range}
   *
   * __NOTE:__ {@link Scenario.period} is intentionally stored in a
   * JSON-compatible format. This function recreates the desired class instances
   * from that JSON-compatible format
   */
  export function getPeriodRange(scenario: Scenario): Range<Date> {
    return new Range<Date>({
      end:
        scenario.period.end !== undefined
          ? new Date(scenario.period.end)
          : undefined,
      includesEnd: scenario.period.includesEnd,
      includesStart: scenario.period.includesStart,
      start:
        scenario.period.start !== undefined
          ? new Date(scenario.period.start)
          : undefined,
    })
  }

  /**
   * Information needed to create a {@link Scenario}
   */
  export type Input = Omit<Scenario, 'createdBy' | 'id'>
  export namespace Input {
    /**
     * Generates a {@link Scenario.Input} with default values, except for any
     * values that are given
     *
     * @example
     * const completelyDefault = Scenario.Input.generate()
     * const mostlyDefault = Scenario.Input.generate({ name: 'My Scenario' })
     */
    export function generate(base: Partial<Scenario> = {}): Scenario.Input {
      // TODO: Possibly change base and return types to Scenario.Input if we
      // manage to support upserting values without id
      // TODO: Fix temporary id that we generate upon upserting scenario
      const {
        alias = '',
        description = '',
        name = '',
        visibility = Visibility.Visible,
      } = base
      return {
        ...base,
        alias,
        description,
        name,
        period: {},
        variables: {},
        visibility,
      }
    }
  }

  /**
   * Information used to update a {@link Scenario}
   */
  export type Updates = Partial<Input>

  /**
   * Determines how the scenario should be presented to all users
   */
  export enum Visibility {
    // NOTE: must be numbers, as that is how this is stored in DynamoDB
    Pinned = 0,
    Visible = 1, // TODO: maybe make this 7 to make room for future values
    Hidden = 2, // TODO: maybe make this 70 to make room for future values
  }
  // Needs to be before namespace is defined
  const VISIBILITIES: ReadonlySet<Visibility> = new Set(
    Object.values(Visibility) as Visibility[],
  )
  export namespace Visibility {
    // NOTE: This line is commented and manually added all visibility enum
    // members, since this line below doesn't returns expected values, it
    // returns array of all keys and all values from this enum, and it is
    // because of the problem with Object.values(Visibility)
    // eg. returns ['Pinned', 'Visible','Hidden', 0, 1, 2]
    // And it is not wanted behavior. When there are strings '0', '1', '2',
    // instead of numbers, then it works, so it is probably problem with
    // handling enums in js.
    // export const ALL: readonly Visibility[] = Array.from(VISIBILITIES)
    export const ALL: readonly Visibility[] = [
      Visibility.Visible,
      Visibility.Pinned,
      Visibility.Hidden,
    ]
    export const isVisibility = TypeChecker.Factory.oneOf(VISIBILITIES)
    export const assertIsVisibility = TypeAsserter.Factory.from(isVisibility)

    /**
     * Returns a user-friendly string representing the given {@link Visibility}
     */
    export function getName(visibility: Visibility): string {
      switch (visibility) {
        case Visibility.Pinned:
          return 'Pinned'
        case Visibility.Visible:
          return 'Visible'
        case Visibility.Hidden:
          return 'Hidden'
      }
    }
  }
}
